import React, { useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { withStyles } from '@mui/styles';
import Dialog from 'libraries/Dialog';
import { observer } from 'mobx-react-lite';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { archiveCandidates, unarchiveCandidates } from 'requests/CandidatesRequests';
import { BATCH_STEP } from 'helper/constants';
import { checkPluralNew } from 'helper/commonFunctions';
import AssessmentEvents from 'events/AssessmentEvents';
import MiscellaneousEvents from 'events/MiscellaneousEvents';
import SnackbarProgress from 'libraries/SnackbarProgress';
import { appCtx } from '../../appStore';
import ArchiveResultsDialog from './ArchiveResultsDialog';

const { CancelToken } = axios;
let source = CancelToken.source();

const styles = () => ({
    loader: {
        color: '#fff'
    }
});

const ArchiveCandidateDialog = observer(({
    classes, open, onClose, callback, dataForEvents = {},
    userAuditionIds, audition, isArchive = true // if false - unarchive
}) => {
    const [loading, setLoading] = useState(false);
    const [showFailed, setShowFailed] = useState(false);
    const [canceled, setCanceled] = useState(false);
    const [error, setError] = useState(false);
    const [currentStepState, setCurrentStepState] = useState(0);
    const [stepsNeededState, setStepsNeededState] = useState(1);
    const [failedList, setFailedList] = useState([]);
    const { flashMessage } = useContext(appCtx);

    useEffect(() => () => {
        onCancel();
    }, []);

    const setInitialState = () => {
        setCurrentStepState(0);
        setStepsNeededState(1);
        setCanceled(false);
    };

    const func = isArchive ? archiveCandidates : unarchiveCandidates;
    const actionLabel = isArchive ? 'Archive' : 'Unarchive';

    const archiveCandidatesFunc = (sentData, currentStep = 1, stepsNeeded, failedListProp) => {
        const nextStartIndex = (currentStep - 1) * BATCH_STEP;
        const nextLastIndex = nextStartIndex + BATCH_STEP;
        source = CancelToken.source();
        return func(sentData.slice(nextStartIndex, nextLastIndex), { cancelToken: source.token })
            .then(({ success, data }) => {
                if (success) {
                    const { failedEmails: newFailedEmails = [] } = data;
                    const updatedFailedList = [...failedListProp, ...newFailedEmails];

                    setCurrentStepState(currentStep);

                    if (currentStep === stepsNeeded) {
                        setLoading(false);
                        setInitialState();
                        if (open) onClose();

                        if (updatedFailedList?.length !== userAuditionIds.length) {
                            if (userAuditionIds.length === 1) {
                                flashMessage(`Candidate ${actionLabel.toLowerCase()}d from the assessment`, 'done');
                            } else {
                                flashMessage(`${userAuditionIds.length - updatedFailedList.length} ${checkPluralNew(userAuditionIds.length - updatedFailedList.length, 'candidate')} ${actionLabel.toLowerCase()}d from the assessment`, 'done');
                            }
                        }
                        if (updatedFailedList.length) {
                            setShowFailed(true);
                            setFailedList(updatedFailedList);
                        } else if (callback) callback();
                    } else if (currentStep < stepsNeeded && !canceled) {
                        archiveCandidatesFunc(sentData, currentStep + 1, stepsNeeded, updatedFailedList);
                    }
                }
            })
            .catch((err) => {
                console.log({ err });
                if ((window.navigator && !window.navigator.onLine) || (err.response && err.response.status.toString()[0] === '5')) {
                    setLoading(false);
                    setInitialState();
                    flashMessage('Something went wrong', 'error');
                    return;
                }

                if (axios.isCancel(err)) {
                    setLoading(false);
                    setInitialState();
                    flashMessage(`${currentStep * BATCH_STEP - failedListProp.length} ${checkPluralNew(userAuditionIds.length - failedListProp.length, 'candidate')} ${actionLabel.toLowerCase()}d from the assessment`, 'done');
                    if (failedListProp.length) {
                        setShowFailed(true);
                        setFailedList(failedListProp);
                    }
                    return;
                }

                if (!err.response) {
                    flashMessage('Something went wrong', 'error');
                    return;
                }

                const errValue = err.response.data.errors;
                if (errValue) {
                    if (typeof errValue === 'string') {
                        setError(errValue || 'Something went wrong');
                    }
                }

                setLoading(false);
                setInitialState();
            });
    };

    const onSubmit = () => {
        setLoading(true);
        const stepsNeeded = Math.ceil(userAuditionIds.length / BATCH_STEP);
        setStepsNeededState(stepsNeeded);
        setFailedList([]);
        const { uuid: ttId, name: ttName } = audition;

        if (userAuditionIds.length > 1) {
            AssessmentEvents.CANDIDATES_BATCH_ACTIONED({
                actionType: isArchive ? 'archive' : 'unarchive',
                numberCandidatesActioned: userAuditionIds.length,
                ttId,
                ttName
            });
            onClose();
        } else if (isArchive) {
            MiscellaneousEvents.ARCHIVED_CANDIDATE(dataForEvents);
        } else {
            MiscellaneousEvents.UNARCHIVED_CANDIDATE(dataForEvents);
        }

        archiveCandidatesFunc(userAuditionIds, 1, stepsNeeded, []);
    };

    const onCancel = () => {
        setCanceled(true);
        source.cancel();
        setLoading(false);
    };

    const header = userAuditionIds.length > 1 ? <span>{actionLabel}&nbsp;{userAuditionIds.length} Candidates</span> : <span>{actionLabel} Candidate</span>;

    return (
        <>
            <SnackbarProgress
                open={loading && stepsNeededState > 1}
                showSuccessIcon
                onCancel={onCancel}
                message={`${actionLabel.slice(0, actionLabel.length - 1)}ing ${userAuditionIds.length} ${checkPluralNew(userAuditionIds.length, 'candidate')}`}
                percents={Math.ceil(currentStepState / stepsNeededState * 100)}
                error={error}
                canceled={canceled}
                errorLabel={error}
            />
            <Dialog
                maxWidth="sm"
                titleComponent={header}
                open={open}
                onClose={onClose}
                handleClose={onClose}
                actionComponent={
                <>
                    <Button
                        className="u-txt--bold u-pdn--lx5 u-pdn--rx5"
                        onClick={onClose}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={onSubmit}
                        disabled={loading}
                        className="u-pdn--lx8 u-pdn--rx8"
                    >
                        {
                            loading ? (
                                <CircularProgress
                                    size={24}
                                    thickness={3}
                                    classes={{ root: classes.loader }}
                                />
                            ) : <span>{header}</span>
                        }
                    </Button>
                </>
                }
            >
                Do you want to {actionLabel.toLowerCase()} the selected {checkPluralNew(userAuditionIds.length, 'candidate')}? {isArchive ? 'Once archived, they can be accessed via the archived tab.' : ''}
            </Dialog>
            <ArchiveResultsDialog
                open={showFailed}
                failed={failedList}
                actionLabel={actionLabel}
                onClose={() => {
                    setShowFailed(false);
                    if (callback) callback();
                }}
            />
        </>
    );
});

export default withStyles(styles)(ArchiveCandidateDialog);
